import React, { useState, useEffect, useCallback, useRef } from 'react';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import { openAlert, dateFormat } from '../../utils/common';
import aiModelService from '../../services/aiModelService';

const MAX_SIZE_MB = 50;
const ALLOWED_EXT = ['.dat', '.bin', '.tflite', '.onnx'];
const META_EXT = ['.meta', '.bin', '.dat'];
const META_SIZE_BYTES = 48;

/**
 * 글로벌(공용) AI 모델 관리 — 사용자별 모델이 없을 때 적용되는 공용 모델.
 * (사용자별 모델은 보정 관리 > 케이스 상세에서 관리)
 * BLE V2.0.7+: 모델 + 48B meta 패키지.
 */
/** 업로드 결과 안내 — 같은 내용이면 앱에 업데이트가 제공되지 않는다 */
function describeUploadResult(data) {
  const d = data || {};
  const hash = String(d.sha256 || '').slice(0, 12);
  if (d.same_as_previous) {
    return `업로드는 됐지만 직전 모델과 내용이 같습니다. 앱에는 업데이트가 제공되지 않습니다. (해시 ${hash})`;
  }
  return `AI 모델 패키지 업로드가 완료되었습니다. 라벨 ${d.version || '-'} · 해시 ${hash}`;
}

const AiModelGlobalPage = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [metaFile, setMetaFile] = useState(null);
  const fileInputRef = useRef();
  const metaInputRef = useRef();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aiModelService.getList(); // userIdx 생략 → 전체(글로벌 포함)
      if (res.success) {
        setItems(res.data?.items || []);
      } else {
        openAlert({ title: '알림', description: res.message || 'AI 모델 목록 조회 실패' });
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'AI 모델 목록 조회 중 오류가 발생했습니다.';
      openAlert({ title: '알림', description: msg });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const validateFile = (f) => {
    if (f.size > MAX_SIZE_MB * 1024 * 1024) return `파일 크기는 ${MAX_SIZE_MB}MB 이하여야 합니다.`;
    const name = f.name.toLowerCase();
    if (!ALLOWED_EXT.some((ext) => name.endsWith(ext))) return `허용된 모델 파일 형식: ${ALLOWED_EXT.join(', ')}`;
    if (/[<>:"/\\|?*]/.test(f.name)) return '파일명에 특수문자를 포함할 수 없습니다.';
    return null;
  };

  const validateMetaFile = (f) => {
    if (f.size !== META_SIZE_BYTES) {
      return `AI Meta 파일은 정확히 ${META_SIZE_BYTES}바이트여야 합니다. (현재 ${f.size}B)`;
    }
    const name = f.name.toLowerCase();
    if (!META_EXT.some((ext) => name.endsWith(ext))) {
      return `허용된 meta 파일 형식: ${META_EXT.join(', ')}`;
    }
    return null;
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const err = validateFile(f);
    if (err) {
      openAlert({ title: '알림', description: err });
      e.target.value = '';
      return;
    }
    setFile(f);
  };

  const handleMetaChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const err = validateMetaFile(f);
    if (err) {
      openAlert({ title: '알림', description: err });
      e.target.value = '';
      return;
    }
    setMetaFile(f);
  };

  const handleUpload = async () => {
    if (!file || !metaFile) {
      openAlert({ title: '알림', description: '모델 파일과 meta 파일(48B)을 모두 선택해주세요.' });
      return;
    }
    if (!window.confirm('글로벌(공용) AI 모델 패키지를 업로드하시겠습니까?')) return;
    setLoading(true);
    try {
      const res = await aiModelService.upload(file, metaFile, null);
      if (res.success) {
        openAlert({ title: '알림', description: describeUploadResult(res.data) });
        setFile(null);
        setMetaFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (metaInputRef.current) metaInputRef.current.value = '';
        fetchList();
      } else {
        openAlert({ title: '알림', description: res.message || '업로드 실패' });
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || '업로드 중 오류가 발생했습니다.';
      openAlert({ title: '알림', description: msg });
    } finally {
      setLoading(false);
    }
  };

  const fmtBytes = (n) => {
    const b = Number(n) || 0;
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <SectionContainer title="글로벌 AI 모델 관리">
      <Spinner visible={loading} />
      <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
        사용자별 모델이 없는 경우 적용되는 <b>공용(글로벌) AI 모델</b>을 관리합니다.
        사용자별 모델은 <b>보정 관리 &gt; 케이스 상세</b>에서 관리하세요.
      </p>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginBottom: 20 }}>
        <h3 style={{ fontSize: 17, marginBottom: 10 }}>글로벌 모델 패키지 업로드</h3>
        <p style={{ color: '#666', fontSize: 14, marginBottom: 12 }}>
          모델({ALLOWED_EXT.join(', ')}, 최대 {MAX_SIZE_MB}MB) + meta({META_SIZE_BYTES}B).
          표시용 라벨은 업로드 시각으로 자동 생성되고, 같은 모델인지는 파일 내용 해시로 판단합니다.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXT.join(',')}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <input
          ref={metaInputRef}
          type="file"
          accept={META_EXT.join(',')}
          onChange={handleMetaChange}
          style={{ display: 'none' }}
        />
        <div style={{ marginBottom: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
            모델 선택
          </button>
          <span style={{ marginLeft: 8, fontSize: 13, color: file ? '#222' : '#999' }}>
            {file ? `${file.name} (${fmtBytes(file.size)})` : '선택된 모델 없음'}
          </span>
        </div>
        <div style={{ marginBottom: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={() => metaInputRef.current?.click()}>
            Meta 선택
          </button>
          <span style={{ marginLeft: 8, fontSize: 13, color: metaFile ? '#222' : '#999' }}>
            {metaFile ? `${metaFile.name} (${metaFile.size}B)` : '선택된 meta 없음'}
          </span>
        </div>
        <button type="button" className="btn btn-primary" onClick={handleUpload} disabled={!file || !metaFile}>
          글로벌 패키지 업로드
        </button>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
        <h3 style={{ fontSize: 17, marginBottom: 10 }}>등록된 AI 모델 (전체)</h3>
        <table className="table" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th>라벨</th>
              <th>해시</th>
              <th>모델</th>
              <th>Meta</th>
              <th>크기</th>
              <th>대상</th>
              <th>상태</th>
              <th>등록일</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: '#888' }}>등록된 모델이 없습니다.</td></tr>
            ) : (
              items.map((m) => (
                <tr key={m.idx}>
                  <td>{m.version || '-'}</td>
                  <td title={m.sha256 || ''}>{m.sha256 ? String(m.sha256).slice(0, 12) : '-'}</td>
                  <td>{m.bin || '-'}</td>
                  <td>{m.meta_bin || '-'}</td>
                  <td>{fmtBytes(m.file_byte)}</td>
                  <td>{m.user_idx ? `사용자 ${m.user_idx}` : '글로벌(공용)'}</td>
                  <td>{m.status || 'ready'}</td>
                  <td>{m.regdate ? dateFormat(m.regdate) : '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </SectionContainer>
  );
};

export default AiModelGlobalPage;
